import React from 'react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Building2, Users, BarChart2, Clock, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageLoadingState } from '@/components/layout/PageLoadingState';
import { DashboardMetricCard } from '@/components/dashboard/DashboardMetricCard';
import AccountSummaryCard from '@/components/accounts/AccountSummaryCard';
import AccountUsersTable from '@/components/accounts/AccountUsersTable';
import AccountHealthBadge from '@/components/accounts/AccountHealthBadge';
import { useAuth } from '@/hooks/useAuthApi';
import { useOrganization } from '@/hooks/useOrganization';
import { useAccountDetail } from '@/hooks/accounts/useAccountDetail';

function formatDuration(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function EnrichmentBadge({ status }: { status: 'pending' | 'enriched' | 'failed' }) {
  if (status === 'enriched') return null; // success state — silent
  if (status === 'pending') {
    return (
      <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-400">
        Enrichment pending
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
      Enrichment unavailable
    </Badge>
  );
}

const AccountDetailPage: React.FC = () => {
  const { account_domain: rawDomain } = useParams<{ account_domain: string }>();
  const accountDomain = rawDomain ? decodeURIComponent(rawDomain) : undefined;

  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading } = useOrganization();

  const { data, isLoading, error } = useAccountDetail(
    currentOrganization?.id,
    accountDomain,
    { users_limit: 100, users_offset: 0 },
  );

  if (authLoading || orgLoading) {
    return <PageLoadingState />;
  }

  if (!user) return <div className="p-4 text-center">Please log in.</div>;
  if (!currentOrganization?.id) return <div className="p-4 text-center">Select an organization.</div>;
  if (!accountDomain) return <div className="p-4 text-center">No account specified.</div>;

  const account = data?.account;
  const users = data?.users ?? [];

  const displayLabel = account?.company_name || accountDomain;

  return (
    <>
      <Helmet>
        <title>{displayLabel} | Accounts | Hobbes</title>
      </Helmet>
      <div className="flex h-full min-h-0 flex-col">
        <PageHeader
          breadcrumbs={[
            { label: 'Accounts', href: '/accounts', icon: Building2 },
            { label: displayLabel },
          ]}
          titleTooltip="Account activity, users, and session evidence."
          border
          metadata={
            account && !isLoading ? (
              <div className="ml-0.5 mt-1 flex flex-col gap-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>{account.account_domain}</span>
                  <EnrichmentBadge status={account.enrichment_status} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <AccountHealthBadge
                    band={account.health_band}
                    lastSeenAt={account.last_seen_at}
                    totalUsers={account.total_users}
                    highIntentUsers={account.high_intent_users}
                    totalSessions={account.total_sessions}
                    size="md"
                  />
                </div>
              </div>
            ) : undefined
          }
        />

        <div className="flex-1 overflow-auto px-4 sm:px-6 md:px-8 py-6 space-y-6">
          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error instanceof Error ? error.message : 'Failed to load account'}
            </div>
          )}

          {/* Summary card */}
          <AccountSummaryCard
            summaryMarkdown={account?.summary_markdown ?? null}
            enrichmentStatus={account?.enrichment_status}
          />

          {/* Metric tiles */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <DashboardMetricCard
              title="Users"
              value={isLoading ? '...' : (account?.total_users ?? 0)}
              icon={Users}
              iconBgColor="bg-blue-100 dark:bg-blue-900/40"
            />
            <DashboardMetricCard
              title="Sessions"
              value={isLoading ? '...' : (account?.total_sessions ?? 0)}
              icon={BarChart2}
              iconBgColor="bg-green-100 dark:bg-green-900/40"
            />
            <DashboardMetricCard
              title="Total Time"
              value={isLoading ? '...' : formatDuration(account?.total_time_spent_seconds ?? 0)}
              icon={Clock}
              iconBgColor="bg-purple-100 dark:bg-purple-900/40"
            />
            <DashboardMetricCard
              title="High-Intent"
              value={isLoading ? '...' : (account?.high_intent_users ?? 0)}
              icon={TrendingUp}
              iconBgColor="bg-orange-100 dark:bg-orange-900/40"
            />
          </div>

          {/* Users list */}
          <div className="rounded-xl border card-border card-bg">
            <div className="border-b border-border px-4 py-3">
              <h2 className="text-sm font-medium text-foreground">
                Users {!isLoading && data ? `(${data.total_users})` : ''}
              </h2>
              <p className="text-xs text-muted-foreground">
                Click a user to view their full session history and transcripts on the People page.
                Use the chevron to expand each user's rolling summary inline.
              </p>
            </div>
            <AccountUsersTable users={users} isLoading={isLoading} offset={0} />
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountDetailPage;
